'use client';

import React, { useState } from 'react';
import BacktestKPICards from './BacktestKPICards';
import BacktestEquityCurve from './BacktestEquityCurve';
import BacktestTradeDistribution from './BacktestTradeDistribution';
import BacktestTradeList from './BacktestTradeList';
import { Save, Download, Loader2 } from 'lucide-react';
import type { BacktestConfigValues } from './BacktestWorkspace';
import { downloadBacktestPDF } from '../utils/backtestPdfExport';

interface BacktestResultsPanelProps {
  params: BacktestConfigValues;
  onSave: () => void;
}

export default function BacktestResultsPanel({ params, onSave }: BacktestResultsPanelProps) {
  const [pdfLoading, setPdfLoading] = useState(false);

  const handleDownloadPDF = async () => {
    setPdfLoading(true);
    try {
      await downloadBacktestPDF(params);
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="space-y-4 animate-slide-up">
      {/* Results header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-positive animate-pulse" />
          <h3 className="text-sm font-600 text-foreground">Backtest Results</h3>
          <span className="text-xs text-muted-foreground">{params.symbol} · {params.timeframe} · {params.startDate} – {params.endDate}</span>
        </div>
        <div className="flex items-center gap-2">
          <button className="text-xs text-primary hover:text-primary/80 font-500 transition-colors px-3 py-1.5 rounded-lg border border-primary/20 hover:border-primary/40">
            Export CSV
          </button>
          <button
            onClick={handleDownloadPDF}
            disabled={pdfLoading}
            className="flex items-center gap-1.5 text-xs text-foreground bg-muted hover:bg-muted/80 disabled:opacity-60 disabled:cursor-not-allowed font-500 transition-colors px-3 py-1.5 rounded-lg border border-border hover:border-border/80"
          >
            {pdfLoading ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Download size={12} />
            )}
            {pdfLoading ? 'Generating…' : 'Download PDF'}
          </button>
          <button
            onClick={onSave}
            className="flex items-center gap-1.5 text-xs text-background bg-primary hover:bg-primary/90 font-500 transition-colors px-3 py-1.5 rounded-lg"
          >
            <Save size={12} />
            Save Run
          </button>
        </div>
      </div>

      <BacktestKPICards />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <BacktestEquityCurve />
        <BacktestTradeDistribution />
      </div>

      <BacktestTradeList />
    </div>
  );
}